'use client';

/**
 * ⚠️ COOKIE-CONSENT-DUPLICATION — read before any non-trivial change.
 *
 * This is the CANONICAL cookie consent bar. It is shared by every monorepo app
 * and by third-party/palette (which imports it via the `layout` path alias).
 *
 * It is NOT the only implementation. Apps under third-party/ that ARE in the
 * yarn workspace import this component directly (third-party/palette and
 * third-party/associate-program). But standalone apps decoupled from the
 * workspace CANNOT import this package and carry their own copy:
 *   • third-party/research/src/components/CookieBar — a manual DUPLICATE port of
 *     this component. It does NOT pick up changes here; keep it in sync.
 *
 * When real privacy settings are implemented here (a LEGAL requirement, not just
 * UI), they must land on EVERY surface at once — do not ship a change here
 * without auditing the duplicate above, and re-check whether any new
 * third-party app needs wiring in. Grep the whole repo for
 * `COOKIE-CONSENT-DUPLICATION` to enumerate every consent surface.
 */

import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import Button from 'ui/src/components/Button';
import IconComponent from 'ui/src/components/Icon';
import PurifiedText from 'ui/src/components/PurifiedText';
import { ThemeEnum } from 'ui/src/components/Theme';
import { useSnackbar } from 'ui/src/components/Snackbar/hooks/useSnackbar';

import { setClientCookie, getClientCookie, COOKIE_CONSENT_NAME } from 'utilities/src/cookiesUtils';

import useCookieBarContext from './hooks/useCookieBarContext';

const PRIVACY_POLICY_URL = 'https://www.bairesdev.com/privacy-policy/#verticalMenu-5-privacy';

const STATEMENT_INTRO =
  'BairesDev LLC uses third-party tracking technologies, including cookies, web beacons and pixels, to personalize your visit to our website, perform marketing, collect analytics, and continuously improve your experience on our website and the services we offer.';

const STATEMENT_FULL = `${STATEMENT_INTRO} Information regarding the cookies we use and their respective purposes is available in our <a href="${PRIVACY_POLICY_URL}" target="_blank" rel="noreferrer" class="font-medium text-site-neutrals-900 underline">Privacy Policy</a>. By clicking "Accept All," you consent to the activation of all non-essential cookies, web beacons and pixels. By clicking "Decline All," you reject all non-essential cookies, web beacons and pixels. Please note that essential cookies will be active on the website even without your consent and cannot be rejected, as they are necessary for the website's operation.`;

function CookieBar() {
  const { cookieBarState, setCookieBarState } = useCookieBarContext();
  const { display, visible } = useSnackbar({ state: cookieBarState, float: true });
  const [expanded, setExpanded] = useState(false);
  const statementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hasCookie = getClientCookie(COOKIE_CONSENT_NAME);

    setCookieBarState(hasCookie ? 'Closed' : 'Visible');
  }, [setCookieBarState]);

  const handleConsent = (value: string) => () => {
    setClientCookie(COOKIE_CONSENT_NAME, value, 365);

    setCookieBarState('Closed');
  };

  // Animate the banner growing from its collapsed (single-line) height to the
  // full statement height. We can't transition to `height: auto`, so we lock
  // the current height, let the content swap, then animate to the measured one.
  const handleReadMore = () => {
    const node = statementRef.current;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (!node || prefersReducedMotion) {
      setExpanded(true);
      return;
    }

    node.style.height = `${node.offsetHeight}px`;
    setExpanded(true);

    requestAnimationFrame(() => {
      if (statementRef.current) statementRef.current.style.height = `${statementRef.current.scrollHeight}px`;
    });
  };

  const handleStatementTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    // Release the fixed height so the text can reflow on resize.
    if (event.propertyName === 'height' && statementRef.current) {
      statementRef.current.style.height = 'auto';
    }
  };

  if (!display || !cookieBarState) return null;

  return (
    <aside className="mdS:px-6 pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4">
      <section
        aria-label="Cookie Preferences"
        className={twMerge(
          'border-site-accent-600 bg-site-neutrals-0 pointer-events-auto flex w-full max-w-[97rem] flex-col items-end gap-4 rounded-t-[1.25rem] border-x border-t p-5 shadow-[0px_32px_64px_-12px_rgba(16,24,40)] will-change-transform',
          'mdS:gap-5 mdS:rounded-t-3xl',
          'mdL:items-center mdL:flex-row mdL:gap-10 mdL:p-6',
          'translate-y-full opacity-0',
          {
            'animate-snackbar-show': visible === true,
            'animate-snackbar-hide': visible === false,
          },
        )}
      >
        <div className="flex w-full min-w-0 flex-1 items-start gap-4">
          <span className="bg-site-neutrals-25 mdS:flex hidden shrink-0 items-center rounded-xl p-1.5">
            {/* @ts-ignore */}
            <IconComponent iconName="Cookie" size="IconM" className="bg-site-primary-600" />
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="site-paragraph-l text-site-neutrals-900 whitespace-nowrap">Cookie Preferences</p>

            <div
              ref={statementRef}
              onTransitionEnd={handleStatementTransitionEnd}
              className="overflow-hidden transition-[height] duration-300 ease-in-out motion-reduce:transition-none"
            >
              {expanded ? (
                <PurifiedText
                  as="p"
                  addAttr={['target', 'rel']}
                  className="site-paragraph-s text-site-neutrals-500"
                >
                  {STATEMENT_FULL}
                </PurifiedText>
              ) : (
                <button
                  type="button"
                  onClick={handleReadMore}
                  className="site-paragraph-s text-site-neutrals-500 flex w-full min-w-0 gap-1 text-left"
                >
                  <span className="min-w-0 flex-1 truncate">{STATEMENT_INTRO}</span>
                  <span className="text-site-neutrals-900 shrink-0 whitespace-nowrap font-medium underline">
                    Read full statement
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <Button<ThemeEnum.Site> variant="Primary" variantColor="Blue" size="Medium" onClick={handleConsent('0')}>
            Decline All
          </Button>

          <Button<ThemeEnum.Site> variant="Primary" variantColor="Blue" size="Medium" onClick={handleConsent('1')}>
            Accept All
          </Button>
        </div>
      </section>
    </aside>
  );
}

export default CookieBar;
